/**
 * The exchange with the server.
 *
 * IndexedDB is the cache, the server is the storage. This carries both
 * directions: what the device has goes up, what the server has comes down,
 * and the merge rules are the same on both sides — the higher `rev` of a text
 * wins, the higher counter of a practised word wins, a solved exercise stays
 * solved.
 *
 * Nothing here is allowed to be loud. A sync that fails is a sync that
 * happens again in two minutes; the app must keep working as if it had not
 * been tried. The only thing the child may notice is that his text is there
 * on the other device.
 */

import * as store from './store.js'
import type { Doc, Word, Mistake, SyncDown } from '../types.js'
import { solvedStock, mergeStockSolved, forgetStock } from '../exercise/stock.js'
import { practiceDays, mergeDays, forgetPractice } from '../exercise/time.js'
import { answers, mergeAnswers, forgetAnswers } from '../exercise/answers.js'

const KEY_STORE = 'kritzl.key'
/**
 * The name that belonged to the key last time.
 *
 * So the greeting is right on a train too: without the network the server
 * cannot be asked, and "Hallo" without a name would be the app forgetting
 * whose it is every time the signal drops.
 *
 * It is a convenience, never a credential — what settles who may see the
 * texts is the key, checked against the server whenever there is one.
 */
const NAME_STORE = 'kritzl.name'

/** The shared key from the link, or '' when this device has not seen one. */
export function apiKey(): string {
  try {
    return localStorage.getItem(KEY_STORE) ?? ''
  } catch {
    return ''
  }
}

/**
 * Takes the key out of `#k=…` and keeps it.
 *
 * Out of the fragment and not the query string: a fragment is not sent to any
 * server and stands in no log. It is removed from the address bar right away,
 * so the key does not travel on in a screenshot or a shared link.
 */
/**
 * Stores a key, or removes it.
 *
 * Only ever through here, so that no place writes the key past `signIn`
 * without having asked the server whether it is one.
 */
function keepKey(key: string): void {
  try {
    if (key) localStorage.setItem(KEY_STORE, key)
    else {
      localStorage.removeItem(KEY_STORE)
      // The name goes with the key: it belonged to that one. The view too.
      localStorage.removeItem(NAME_STORE)
      localStorage.removeItem(VIEW_STORE)
    }
  } catch {}
}

/**
 * Whose view this is, from the last successful check.
 *
 * A parent key opens the child's Kritzl. The server says so once, with the
 * name; the app has to remember it between starts, because the same two
 * things that need the name offline — the greeting and the question whether
 * anything done here counts — need this too.
 */
const VIEW_STORE = 'kritzl.view'

export function lastView(): { parent: boolean; viewer: string } {
  try {
    const raw = localStorage.getItem(VIEW_STORE)
    const v = raw ? (JSON.parse(raw) as { parent?: unknown; viewer?: unknown }) : null
    return { parent: v?.parent === true, viewer: typeof v?.viewer === 'string' ? v.viewer : '' }
  } catch {
    return { parent: false, viewer: '' }
  }
}

function keepView(parent: boolean, viewer: string): void {
  try {
    localStorage.setItem(VIEW_STORE, JSON.stringify({ parent, viewer }))
  } catch {}
}

/** The name from the last successful check, or '' when there is none. */
export function lastName(): string {
  try {
    return localStorage.getItem(NAME_STORE) ?? ''
  } catch {
    return ''
  }
}

function keepName(name: string): void {
  try {
    localStorage.setItem(NAME_STORE, name)
  } catch {}
}

/**
 * Asks the server whom a key belongs to.
 *
 * This is the check AND the greeting: without it a mistyped key could not be
 * told from a valid one — the app would store it and only fail quietly at the
 * next sync.
 *
 * @returns the name, or null when the key is not one. Throws when the server
 *   cannot be reached, which is a different case and reads differently on
 *   screen: a wrong key is the child's problem, a dead network is not.
 */
export function whoAmI(key = apiKey()): Promise<string | null> {
  const p = ask(key)
  checking = p.catch(() => null)
  return p
}

/**
 * The check in flight, if one is. The sync waits for it — see `syncNow`.
 */
let checking: Promise<unknown> | null = null

/** The draft on the paper — `App.tsx` writes it under this name. */
export const DRAFT_STORE = 'kritzl.v1'

/**
 * Whether the device was just emptied because the key opens another Kritzl.
 *
 * Read by the app after a check; it then starts over, so that nothing on the
 * screen still comes from before.
 */
let wiped = false
export const deviceWasWiped = (): boolean => wiped

async function ask(key: string): Promise<string | null> {
  if (!key) return null
  const res = await fetch('/api/me', { headers: { 'x-kritzl-key': key } })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`me ${res.status}`)
  const { name, parent, viewer } =
    (await res.json()) as { name?: string; parent?: boolean; viewer?: string }
  // A key that has just become a parent's key — at sign-in, or because the
  // server says so of a key that used to open the holder's own Kritzl —
  // turns this device into a mirror of the child's. Whatever is on it from
  // before would otherwise go up under the child's name: findings and pages
  // are the two things a parent may send. So it goes, all of it. The child's
  // things come down with the next sync; the server has every one of them.
  if (parent === true && !lastView().parent) {
    await wipeDevice()
    wiped = true
  }
  keepName(name ?? '')
  keepView(parent === true, viewer ?? '')
  return name ?? ''
}

/** Everything of a person's, off this device. Settings and fonts stay. */
async function wipeDevice(): Promise<void> {
  await store.wipeContent()
  forgetStock()
  forgetPractice()
  forgetAnswers()
  try {
    localStorage.removeItem(DRAFT_STORE)
  } catch {}
}

/**
 * Takes a key the child pasted in.
 *
 * The whole link is allowed, not just the key: what arrives by messenger is a
 * link, and tapping it may well open the wrong browser. Pasting it has to
 * work, so the key is fished out of whatever comes.
 *
 * @returns the name on success, null when the key is not one.
 */
export async function signIn(input: string): Promise<string | null> {
  const key = keyFromText(input)
  if (!key) return null
  const name = await whoAmI(key)
  if (name === null) return null
  keepKey(key)
  return name
}

/** Forgets the key. The texts on the device stay — they are not the key's. */
export function signOut(): void {
  keepKey('')
}

/**
 * What a password may look like: letters and digits, six at least.
 *
 * Umlauts stay out on purpose. A password is typed on a strange keyboard
 * sooner or later, and „schön" is then the one thing that cannot be typed.
 */
const PASSWORD = /[a-z0-9]{6,}/i

/**
 * Fishes the password out of pasted text.
 *
 * A bare password, a whole link, a link with other parameters — all the same
 * to this: what arrives by messenger is a link, and tapping it may well open
 * the wrong browser, so pasting has to work.
 *
 * Lower-cased, and that is where case-insensitivity is decided ONCE. A phone
 * capitalises the first letter by itself, and a capitalised password failing to open the
 * app would be the app's fault, not the child's. The key tool lower-cases
 * before hashing for the same reason, so both ends agree.
 */
export function keyFromText(text: string): string {
  const t = text.trim()
  const fromLink = t.match(new RegExp(`[#&?]k=(${PASSWORD.source})`, 'i'))
  if (fromLink?.[1]) return fromLink[1].toLowerCase()
  return new RegExp(`^${PASSWORD.source}$`, 'i').test(t) ? t.toLowerCase() : ''
}

export function takeKeyFromUrl(): void {
  const m = location.hash.match(new RegExp(`[#&]k=(${PASSWORD.source})`, 'i'))
  // Group 1 exists whenever the regex matches.
  const key = m?.[1]
  if (!key) return
  keepKey(key.toLowerCase())
  history.replaceState(null, '', location.pathname + location.search)
}

async function call(method: 'GET'): Promise<SyncDown | null>
async function call(method: 'POST', body: unknown): Promise<unknown>
async function call(method: string, body?: unknown): Promise<unknown> {
  const key = apiKey()
  if (!key) return null
  const res = await fetch('/api/sync', {
    method,
    headers: { 'content-type': 'application/json', 'x-kritzl-key': key },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error(`sync ${res.status}`)
  return res.json()
}

/**
 * One full round: everything up, everything down.
 *
 * Up first. A device that has just written must not have its text overwritten
 * by an older one from the server — sending first means the server already
 * knows the newer revision when it answers.
 *
 * @returns true when something came down that the screen should show.
 */
export async function syncNow(): Promise<boolean> {
  if (!apiKey()) return false
  // Not before the server has said whose key this is. At start the check and
  // the first round set off together, and a key that has just become a
  // parent's would send this device's findings up under the child's name
  // before the check could empty it.
  await checking


  const [texts, words, mistakes] = await Promise.all([
    // allDocs and not recent: the deleted ones must go up too, otherwise the
    // deletion never leaves this device. The same for the pages.
    store.allDocs(), store.corrections(), store.mistakes(),
  ])
  const pageRows = await store.allPages().catch(() => [])

  // A parent sends what a parent may: the pages and their findings. Not the
  // texts, not the practised words, not a solved mark, not a minute. The
  // server drops all of it anyway; holding it back here keeps the request
  // honest and small.
  const parent = lastView().parent

  await call('POST', parent ? {
    mistakes: mistakes.map((m) => ({ ...m, solved: null, stage: null })),
    pages: pageRows,
  } : {
    texts: texts.map((t) => ({
      id: t.id, title: t.title, body: t.body, personal: t.personal,
      created: t.created, changed: t.changed, rev: t.rev, deleted: t.deleted,
      fixes: t.fixes,
    })),
    words: words.map((w) => ({
      key: w.key, wrong: w.wrong, right: w.right, rule: w.rule,
      count: w.count, solo: w.solo ?? 0, first: w.first, last: w.last,
      practisedAt: w.practisedAt ?? null, context: w.context ?? '',
      sentence: w.sentence ?? '', sentenceWrong: w.sentenceWrong ?? '',
      sentenceRight: w.sentenceRight ?? '', sentenceKind: w.sentenceKind ?? '',
      sentenceGen: w.sentenceGen ?? 0, sentenceMore: w.sentenceMore ?? [],
      sentenceStrategy: w.sentenceStrategy ?? '', sentenceTip: w.sentenceTip ?? '',
    })),
    mistakes,
    // The stock exercises done here. Ids only — the catalogue is in the app.
    stockSolved: [...solvedStock()],
    // The minutes per day and every answer given. Both only ever grow, so
    // there is nothing to resolve — see the two merges below.
    practiceDays: practiceDays(),
    answers: answers(),
    pages: pageRows,
  })

  const down = await call('GET')
  if (!down) return false
  return applyDown(down, { texts, words, mistakes, pages: pageRows })
}

/**
 * Puts down what the server sent, where it is newer.
 *
 * @returns true when anything on the screen has to be drawn again.
 */
async function applyDown(
  down: SyncDown,
  mine: { texts: Doc[]; words: Word[]; mistakes: Mistake[]; pages: store.Page[] }
): Promise<boolean> {
  let changed = false

  const known = new Map(mine.texts.map((t) => [t.id, t]))
  for (const t of down.texts ?? []) {
    const here = known.get(t.id)
    // Equal revisions mean the same text: this device sent it a moment ago.
    if (here && (here.rev ?? 1) >= (t.rev ?? 1)) continue
    await store.putDoc({
      id: t.id, title: t.title ?? '', body: t.body ?? '',
      personal: Array.isArray(t.personal) ? t.personal : [],
      created: t.created ?? Date.now(), changed: t.changed ?? Date.now(),
      rev: t.rev ?? 1, deleted: t.deleted ?? null,
      fixes: t.fixes ?? undefined,
    })
    changed = true
  }

  const myWords = new Map(mine.words.map((w) => [w.key, w]))
  for (const w of down.words ?? []) {
    const here = myWords.get(w.key)
    // Take it down also when the other side practised it and this one has not
    // yet: otherwise this device puts the exercise up again right away.
    const practisedThere = w.practisedAt != null && here?.practisedAt == null
    // Take it down for a newly arrived exercise sentence as well.
    // Also take it down when the other side has a NEWER generation: an old
    // sentence must be replaceable, not only a missing one.
    const sentenceThere = !!w.sentence &&
      (!here?.sentence || (w.sentenceGen ?? 0) > (here.sentenceGen ?? 0))
    if (!practisedThere && !sentenceThere && here &&
        (here.count ?? 0) >= (w.count ?? 0) && (here.last ?? 0) >= (w.last ?? 0)) continue
    await store.putWord(w)
    changed = true
  }

  // The stock progress only ever grows: whoever practised a sentence on the
  // phone has practised it, and this device must not undo that.
  if (mergeStockSolved(down.stockSolved ?? [])) changed = true

  // The same for the figures: minutes per day take the larger value, answers
  // are added. Neither can be taken back by a device that knows less.
  if (mergeDays(down.practiceDays ?? {})) changed = true
  if (mergeAnswers(down.answers ?? [])) changed = true

  // The pages. A transcript never changes, so only a new one or a newly
  // deleted one is worth writing.
  const myPages = new Map(mine.pages.map((p) => [p.id, p]))
  for (const pg of down.pages ?? []) {
    const here = myPages.get(pg.id)
    if (here && !!here.deleted === !!pg.deleted) continue
    await store.putPage(pg)
    changed = true
  }

  // A page deleted elsewhere takes its mistakes with it here too.
  //
  // The server removes them and refuses to take them back, but this device
  // still holds its copies — and the exercises read the copies. Without this
  // a page thrown away on the PC kept putting its words up on the phone.
  const gone = new Set((down.pages ?? []).filter((p) => p.deleted).map((p) => p.id))
  const stale = mine.mistakes.filter((m) => gone.has(m.pageId))
  if (stale.length) {
    await store.removeMistakes(stale.map((m) => m.id))
    changed = true
  }

  const myMistakes = new Map(mine.mistakes.map((m) => [m.id, m]))
  for (const m of down.mistakes ?? []) {
    if (gone.has(m.pageId)) continue
    const here = myMistakes.get(m.id)
    // Solved stays solved on both sides; only a new finding or a newly solved
    // one is worth writing.
    if (here && here.solved === m.solved && here.sentence === m.sentence &&
        (here.sentenceGen ?? 0) >= (m.sentenceGen ?? 0)) continue
    await store.putMistake(m)
    changed = true
  }

  return changed
}

/**
 * Syncs, and keeps syncing.
 *
 * On coming back online and on coming back to the tab, because those are the
 * two moments when the other device may have written something. Plus a slow
 * beat in between for the case where the app just stays open.
 *
 * @param onChange  Called when something came down that changes the screen.
 */
export function startSync(onChange?: () => void): () => Promise<void> {
  let running = false
  const run = async () => {
    if (running || !apiKey()) return
    running = true
    try {
      if (await syncNow()) onChange?.()
    } catch {
      // Offline, or the server is having a moment. The next beat tries again.
    } finally {
      running = false
    }
  }

  run()
  setInterval(run, 120_000)
  addEventListener('online', run)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) run()
  })

  return run
}
